import { LineChart, Pencil, Trash2, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableActionsCell,
  TableActionsHead,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TablePagination } from "@/components/ui/table-pagination";
import { FEATURE_FLAGS } from "@/lib/feature-flags";
import { useFeatureFlag } from "@/lib/hooks/use-feature-flags";
import { usePagination } from "@/lib/hooks/use-pagination";
import {
  usePortfolioCollection,
  useProducts,
  useToggleProductActive,
} from "@/lib/hooks/use-portfolio-collections";
import { useUserLanguage } from "@/lib/i18n/context";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { TableCardSkeleton } from "../../skeleton-loaders";
import { InstrumentSyncDot } from "../instrument-sync-status";
import { ProductAnalysisDialog } from "../product-analysis-dialog";
import { ProductWarningBadge } from "../product-warning-badge";
import { useProductsTabContext } from "../products-tab-context";
import { T } from "./page.i18n";

export default function ProductsGeneralPage() {
  const {
    collectionId,
    debouncedSearch,
    openEdit,
    openDelete,
    loadingProductId,
    checkingDeleteId,
  } = useProductsTabContext();
  const { products, isLoading } = useProducts(collectionId, debouncedSearch);
  const { collection } = usePortfolioCollection(collectionId);
  const { userLanguage } = useUserLanguage();
  const t = useT(T);
  const localize = useLocalize();
  const toggleProductActive = useToggleProductActive();
  // X-Ray is the only analysis a product without a linked instrument can still
  // produce (it is keyed off the ISIN alone), and the server gates it on this flag.
  // Fail closed while the flag query is in flight: the button appears once the flag
  // resolves rather than being offered and then withdrawn (and clicked in between,
  // which would open the dialog on its empty state).
  const { enabled: xrayAvailable } = useFeatureFlag(FEATURE_FLAGS.MORNINGSTAR_XRAY);
  const [analysisProduct, setAnalysisProduct] = useState<{
    id: string;
    name: string;
    isin: string | null;
    orderAmountType: "amount" | "units";
    ticker: string | null;
    dataset: string | null;
  } | null>(null);

  const {
    page,
    setPage,
    pageSize,
    totalCount,
    totalPages,
    pageItems: paginatedProducts,
  } = usePagination(products, 10);

  const handleToggleActive = (productId: string) => {
    toggleProductActive.mutate(
      { collectionId, productId },
      {
        onError: (err: Error) => {
          toast.error(err.message || t.failedToUpdateStatus);
        },
      },
    );
  };

  if (isLoading && products.length === 0 && !debouncedSearch) {
    return <TableCardSkeleton columns={5} />;
  }

  if (products.length === 0 && debouncedSearch.trim() !== "") {
    return <EmptyState>{t.noProductsMatch}</EmptyState>;
  }

  if (products.length === 0) {
    return <EmptyState>{t.noProducts}</EmptyState>;
  }

  // Per-row wrapper-type warnings only apply once the collection actually has
  // active wrapper types — until then the missing config itself is the problem
  // (flagged on the Wrapper Types tab instead).
  const warnOnMissingWrapperTypes = collection?.wrapperTypeStatus?.hasWrapperTypes === true;

  return (
    <>
      {warnOnMissingWrapperTypes &&
        collection?.wrapperTypeStatus?.hasProductsMissingWrapperTypes && (
          <Alert variant="warning">
            <AlertDescription className="flex items-center gap-2">
              <TriangleAlert className="h-4 w-4 shrink-0 text-warning" />
              {t.productsMissingWrapperTypes}
            </AlertDescription>
          </Alert>
        )}
      <Table variant="data">
        <TableHeader>
          <TableRow>
            <TableHead>{t.productName}</TableHead>
            <TableHead>{t.isin}</TableHead>
            <TableHead>{t.instrument}</TableHead>
            <TableHead>{t.active}</TableHead>
            <TableActionsHead>{t.actions}</TableActionsHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {paginatedProducts.map((product) => {
            const hasInstrument = !!(product.ticker && product.dataset);
            const warnings =
              warnOnMissingWrapperTypes && product.wrapperTypeIds.length === 0
                ? [t.wrapperTypeMissing]
                : [];
            // Nothing to analyse without an instrument (no historical return, no vendor
            // sustainability data) unless the ISIN can still resolve an X-Ray.
            const canAnalyse = hasInstrument || (!!product.isin && xrayAvailable);
            return (
              <TableRow key={product.id}>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    {localize(product.nameI18n)}
                    <ProductWarningBadge warnings={warnings} />
                  </div>
                </TableCell>
                <TableCell>
                  {product.isin ? (
                    <code className="text-xs bg-muted px-2 py-1 rounded">{product.isin}</code>
                  ) : (
                    <span className="text-muted-foreground italic text-xs">—</span>
                  )}
                </TableCell>
                <TableCell>
                  {hasInstrument ? (
                    <div className="flex items-center gap-2">
                      <InstrumentSyncDot health={product.instrumentSync} lang={userLanguage} />
                      <code className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                        {product.dataset}:{product.ticker}
                      </code>
                    </div>
                  ) : (
                    <span className="text-muted-foreground italic text-xs">{t.notLinked}</span>
                  )}
                </TableCell>
                <TableCell>
                  <Switch
                    aria-label={`${t.active}: ${localize(product.nameI18n)}`}
                    checked={product.isActive}
                    onCheckedChange={() => handleToggleActive(product.id)}
                  />
                </TableCell>
                <TableActionsCell>
                  {canAnalyse && (
                    <Button
                      variant="ghost"
                      size="icon"
                      tooltip={t.viewAnalysis}
                      onClick={() =>
                        setAnalysisProduct({
                          id: product.id,
                          name: localize(product.nameI18n),
                          isin: product.isin,
                          orderAmountType: product.orderAmountType,
                          ticker: product.ticker ?? null,
                          dataset: product.dataset ?? null,
                        })
                      }
                    >
                      <LineChart className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    tooltip={t.edit}
                    onClick={() => openEdit(product.id)}
                    loading={loadingProductId === product.id}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    tooltip={t.delete}
                    loading={checkingDeleteId === product.id}
                    onClick={() => openDelete({ id: product.id, nameI18n: product.nameI18n })}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableActionsCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <TablePagination
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        totalPages={totalPages}
        onPageChange={setPage}
      />

      <ProductAnalysisDialog
        open={analysisProduct !== null}
        collectionId={collectionId}
        productId={analysisProduct?.id ?? null}
        productName={analysisProduct?.name ?? ""}
        isin={analysisProduct?.isin ?? null}
        orderAmountType={analysisProduct?.orderAmountType ?? "amount"}
        ticker={analysisProduct?.ticker ?? null}
        dataset={analysisProduct?.dataset ?? null}
        onClose={() => setAnalysisProduct(null)}
      />
    </>
  );
}
